import { motion } from 'framer-motion'
import { ArrowRight, Shield, Zap, Globe, Users, Star, CheckCircle, Sparkles, Crown, Award, TrendingUp, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import UptimeIndicator from '@/components/features/status/UptimeIndicator'
import LiveMetrics from '@/components/features/dashboard/LiveMetrics'
import NetworkParticles from '@/components/home/NetworkParticles'

export default function Home() {
  const navigate = useNavigate()

  const features = [
    {
      icon: Shield,
      title: 'Secure Architectures',
      description:
        'Walk through Aruba Central and ClearPass policy journeys tailored to common channel-led security requirements.'
    },
    {
      icon: Zap,
      title: 'Performance Narratives',
      description:
        'Use live dashboards and assurance tooling to tell the story behind wireless, switching, and routing outcomes.'
    },
    {
      icon: Globe,
      title: 'Channel Ecosystem Hub',
      description:
        'Access aggregated demo runbooks, vendor collateral, and enablement resources maintained for Westcoast partners.'
    },
    {
      icon: Users,
      title: 'Presales Specialists',
      description:
        'Collaborate with Westcoast architects who co-design every session around your customer objectives.'
    }
  ]

  return (
    <div className="min-h-screen animated-gradient-bg relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-20 left-0 w-96 h-96 bg-blue-500/10 dark:bg-cyan-400/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 dark:bg-pink-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 dark:bg-purple-400/5 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* Main content */}
      <div className="relative z-10 pt-20 pb-16">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 text-center mb-20">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            {/* Amazing Interactive Network Banner */}
            <div className="mb-16">
              <NetworkParticles />
            </div>

            {/* Professional Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-gradient-to-r from-[#BBD134] to-green-600 shadow-2xl mb-8 group"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="h-5 w-5 text-white" />
              </motion.div>
              <span className="text-sm font-black text-white uppercase tracking-widest">
                Technology Solutions Lab
              </span>
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 rounded-full bg-white"
              />
            </motion.div>

            {/* Enhanced Title */}
            <motion.h2
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-7xl font-black mb-6 py-2 leading-tight"
            >
              <span className="text-gray-900 dark:text-white">
                Plan Your Partner-Led
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#BBD134] via-green-500 to-emerald-600 bg-clip-text text-transparent">
                Lab Session
              </span>
            </motion.h2>

            {/* Description with better spacing */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-medium"
            >
              Experience guided Aruba and Juniper demonstrations curated for the Westcoast channel community. As one of Europe&apos;s leading distributors, we align vendors and resellers with the right experts, validated architectures, and enablement resources.
            </motion.p>

            {/* Enhanced Trust Indicators with Icons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ y: -8, scale: 1.02, borderColor: 'rgba(96, 165, 250, 0.6)', transition: { duration: 0.2 } }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="glass-morphism-dark rounded-2xl p-6 border-2 border-blue-200/50 dark:border-blue-400/30 hover:shadow-2xl hover:shadow-blue-500/20 relative overflow-hidden group cursor-pointer"
                style={{ willChange: 'transform' }}
              >
                {/* Glass effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-2xl pointer-events-none" />

                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg"
                  >
                    <Users className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </motion.div>
                  <div className="text-2xl font-black mb-2 bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                    Channel
                  </div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    Partner Access
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Vendor and reseller-led sessions
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ y: -8, scale: 1.02, borderColor: 'rgba(74, 222, 128, 0.6)', transition: { duration: 0.2 } }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="glass-morphism-dark rounded-2xl p-6 border-2 border-green-200/50 dark:border-green-400/30 hover:shadow-2xl hover:shadow-green-500/20 relative overflow-hidden group cursor-pointer"
                style={{ willChange: 'transform' }}
              >
                {/* Glass effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-2xl pointer-events-none" />

                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg"
                  >
                    <Star className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </motion.div>
                  <div className="text-2xl font-black mb-2 bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
                    Curated
                  </div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    Session Design
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Agendas prepared with our architects
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ y: -8, scale: 1.02, borderColor: 'rgba(192, 132, 252, 0.6)', transition: { duration: 0.2 } }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="glass-morphism-dark rounded-2xl p-6 border-2 border-purple-200/50 dark:border-purple-400/30 hover:shadow-2xl hover:shadow-purple-500/20 relative overflow-hidden group cursor-pointer"
                style={{ willChange: 'transform' }}
              >
                {/* Glass effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-2xl pointer-events-none" />

                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg"
                  >
                    <Award className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </motion.div>
                  <div className="text-2xl font-black mb-2 bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                    Proven
                  </div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    Demo Scenarios
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Based on live customer outcomes
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ y: -8, scale: 1.02, borderColor: 'rgba(251, 146, 60, 0.6)', transition: { duration: 0.2 } }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="glass-morphism-dark rounded-2xl p-6 border-2 border-orange-200/50 dark:border-orange-400/30 hover:shadow-2xl hover:shadow-orange-500/20 relative overflow-hidden group cursor-pointer"
                style={{ willChange: 'transform' }}
              >
                {/* Glass effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-2xl pointer-events-none" />

                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg"
                  >
                    <Crown className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </motion.div>
                  <div className="text-2xl font-black mb-2 bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                    Specialist
                  </div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    Support
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Certified presales engineers
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  onClick={() => navigate('/booking')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl shadow-2xl transform transition-all duration-200 group"
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  Book Your Demo Session
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/demos')}
                  className="glass-morphism-dark border-2 border-blue-400/30 hover:border-blue-400/50 font-semibold py-4 px-8 rounded-xl backdrop-blur-lg"
                >
                  <Globe className="mr-2 h-5 w-5" />
                  Explore Technologies
                </Button>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/about')}
                  className="glass-morphism-dark border-2 border-purple-400/30 hover:border-purple-400/50 font-semibold py-4 px-8 rounded-xl backdrop-blur-lg"
                >
                  <Info className="mr-2 h-5 w-5" />
                  About Technology Solutions
                </Button>
              </motion.div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mt-6 max-w-3xl mx-auto">
              Prepare ahead with our consolidated vendor documentation, demo playbooks, and event calendar—everything is maintained for partners inside the Lab platform.
            </p>
          </motion.div>
        </div>

        {/* Branded Showcase Section - Two Images Side by Side */}
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Image 1 - Wide (takes 3 columns) */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative group h-full"
              >
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#BBD134]/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Main image container */}
                <div className="relative glass-morphism-dark rounded-2xl overflow-hidden border-2 border-[#BBD134]/30 hover:border-[#BBD134]/60 transition-all duration-500 shadow-2xl h-full">
                  {/* Accent bar with brand color */}
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#BBD134] via-blue-500 to-purple-500 z-10" />

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="relative h-full"
                  >
                    <img
                      src="/images/branded/1.png"
                      alt="Westcoast Technology Solutions Lab"
                      width={1200}
                      height={675}
                      className="w-full h-full object-cover"
                    />

                    {/* Overlay gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Interactive corner accents */}
                    <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-[#BBD134] opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                    <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-[#BBD134] opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                  </motion.div>

                  {/* Caption bar */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80 backdrop-blur-md p-4 lg:p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg lg:text-xl font-bold text-white mb-1">
                          Technology Solutions Lab
                        </h3>
                        <p className="text-xs lg:text-sm text-gray-300">
                          Enterprise-grade networking in our state-of-the-art facility
                        </p>
                      </div>
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-[#BBD134] to-blue-500 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <Sparkles className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Floating stat badges */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="absolute -left-2 lg:-left-4 top-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-[#BBD134]/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                >
                  <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#BBD134] to-green-600 bg-clip-text text-transparent">
                    AI-Driven
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Next-Gen Tech</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="absolute -right-2 lg:-right-4 bottom-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-blue-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                >
                  <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                    Cloud-Native
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Infrastructure</div>
                </motion.div>
              </motion.div>
            </div>

            {/* Image 2 - Tall (takes 2 columns) */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative group h-full"
              >
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 via-[#BBD134]/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Main image container */}
                <div className="relative glass-morphism-dark rounded-2xl overflow-hidden border-2 border-purple-400/30 hover:border-purple-400/60 transition-all duration-500 shadow-2xl h-full">
                  {/* Accent bar with brand color */}
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-500 via-[#BBD134] to-blue-500 z-10" />

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="relative h-full"
                  >
                    <img
                      src="/images/branded/2.png"
                      alt="Westcoast Innovation Showcase"
                      width={1200}
                      height={1600}
                      className="w-full h-full object-cover"
                    />

                    {/* Overlay gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Interactive corner accents */}
                    <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-purple-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                    <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-purple-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                  </motion.div>

                  {/* Caption bar */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80 backdrop-blur-md p-4 lg:p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-10">
                    <div className="flex flex-col gap-3">
                      <div>
                        <h3 className="text-lg lg:text-xl font-bold text-white mb-1">
                          Innovation Hub
                        </h3>
                        <p className="text-xs lg:text-sm text-gray-300">
                          Where technology meets expertise
                        </p>
                      </div>
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className="w-10 h-10 bg-gradient-to-r from-purple-500 to-[#BBD134] rounded-full flex items-center justify-center self-end"
                      >
                        <Sparkles className="w-5 h-5 text-white" />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Floating stat badges */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="absolute -left-2 lg:-left-4 top-1/3 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-purple-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                >
                  <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">
                    Innovation
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Driven Forward</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="absolute -right-2 lg:-right-4 bottom-1/3 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-[#BBD134]/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                >
                  <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#BBD134] to-green-600 bg-clip-text text-transparent">
                    Expert-Led
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Solutions</div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Live Metrics Dashboard */}
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <LiveMetrics />
        </div>

        {/* Enhanced Features Section */}
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-black dark:text-white">
              Tailored Demonstrations for Every Objective
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-12">
              Every engagement leverages immersive demonstrations and expert storytelling—we align the session to your specific goals and technical requirements.
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="glass-morphism-dark rounded-xl p-6 border border-blue-200/50 dark:border-blue-400/30 hover:border-blue-400/50 dark:hover:border-blue-400/50 group cursor-pointer"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-black dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    {feature.description}
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                      Presales Expertise
                    </span>
                  </div>
                </motion.div>
              )
            })}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-center text-sm text-gray-600 dark:text-gray-400 mb-16"
          >
            Learn more about our capabilities, methodology, and how we support the entire ecosystem on the About page.
          </motion.p>

          {/* Comparison Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-morphism-dark rounded-xl p-8 border border-green-200/50 dark:border-green-400/30 bg-green-500/5"
          >
            <h3 className="text-2xl font-bold mb-4 text-center text-black dark:text-white">
              Why Technology Solutions Lab Stands Out
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <Star className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                <h4 className="font-bold text-black dark:text-white mb-2">Production-Grade Infrastructure</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Live vendor platforms and real-world configurations powering every session
                </p>
              </div>
              <div className="text-center">
                <Crown className="w-8 h-8 text-purple-500 mx-auto mb-3" />
                <h4 className="font-bold text-black dark:text-white mb-2">Specialist Expertise</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Certified presales architects delivering technical depth and solution insight
                </p>
              </div>
              <div className="text-center">
                <Award className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                <h4 className="font-bold text-black dark:text-white mb-2">Ecosystem Enablement</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Supporting the channel with resources that accelerate business development
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Lab Status Overview */}
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6 text-black dark:text-white">
              Infrastructure Health
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Real-time monitoring with live performance trends from the past 24 hours
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <UptimeIndicator />
          </div>
        </div>


        {/* Technology Partnership Benefits */}
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6 text-black dark:text-white">
              Industry-Leading Technology Partners
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-12">
              Experience cutting-edge networking solutions from the world's most trusted vendors
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* HPE Partnership */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl p-6 border-2 border-[#01A982]/30 bg-gradient-to-br from-[#01A982]/5 via-transparent to-transparent backdrop-blur-sm hover:border-[#01A982]/60 hover:shadow-[0_0_40px_rgba(1,169,130,0.3)] group"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#01A982]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#01A982]/20 blur-3xl rounded-full -translate-y-16 translate-x-16 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform duration-700" />

              <div className="relative z-10">
                {/* Logo Section - Centered */}
                <div className="flex flex-col items-center mb-6">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5 }}
                    className="h-24 flex items-center justify-center"
                  >
                    <img src="/vendors/HPE-dark.png" alt="HPE" width={200} height={56} className="max-h-14 w-auto drop-shadow-2xl block dark:hidden" />
                    <img src="/vendors/HPE-light.png" alt="HPE" width={200} height={56} className="max-h-14 w-auto drop-shadow-2xl hidden dark:block" />
                  </motion.div>
                  <div className="mt-3 text-center">
                    <h3 className="text-2xl font-black text-black dark:text-white mb-1 tracking-tight">HPE</h3>
                    <div className="h-1 w-12 mx-auto bg-gradient-to-r from-[#01A982] to-[#00C8B4] rounded-full mb-2" />
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Enterprise Infrastructure</p>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-2.5">
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#01A982]/20 flex items-center justify-center group-hover/item:bg-[#01A982]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#01A982]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Complete server solutions</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#01A982]/20 flex items-center justify-center group-hover/item:bg-[#01A982]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#01A982]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Storage & hyperconverged</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#01A982]/20 flex items-center justify-center group-hover/item:bg-[#01A982]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#01A982]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Edge-to-cloud computing</span>
                  </motion.li>
                </ul>
              </div>
            </motion.div>

            {/* Aruba Partnership */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl p-6 border-2 border-[#FF6C2F]/30 bg-gradient-to-br from-[#FF6C2F]/5 via-transparent to-transparent backdrop-blur-sm hover:border-[#FF6C2F]/60 hover:shadow-[0_0_40px_rgba(255,108,47,0.3)] group"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6C2F]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF6C2F]/20 blur-3xl rounded-full -translate-y-16 translate-x-16 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform duration-700" />

              <div className="relative z-10">
                {/* Logo Section - Centered */}
                <div className="flex flex-col items-center mb-6">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5 }}
                    className="h-24 flex items-center justify-center"
                  >
                    <img src="/vendors/aruba.png" alt="HPE Aruba" width={300} height={96} className="max-h-24 w-auto dark:invert drop-shadow-2xl" />
                  </motion.div>
                  <div className="mt-3 text-center">
                    <h3 className="text-2xl font-black text-black dark:text-white mb-1 tracking-tight">HPE Aruba</h3>
                    <div className="h-1 w-12 mx-auto bg-gradient-to-r from-[#FF6C2F] to-[#FF8C5F] rounded-full mb-2" />
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Wireless & Networking</p>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-2.5">
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF6C2F]/20 flex items-center justify-center group-hover/item:bg-[#FF6C2F]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#FF6C2F]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Wi-Fi 6E & wireless security</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF6C2F]/20 flex items-center justify-center group-hover/item:bg-[#FF6C2F]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#FF6C2F]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Network access control</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF6C2F]/20 flex items-center justify-center group-hover/item:bg-[#FF6C2F]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#FF6C2F]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Cloud-native management</span>
                  </motion.li>
                </ul>
              </div>
            </motion.div>

            {/* Juniper Partnership */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl p-6 border-2 border-[#0096D6]/30 bg-gradient-to-br from-[#0096D6]/5 via-transparent to-transparent backdrop-blur-sm hover:border-[#0096D6]/60 hover:shadow-[0_0_40px_rgba(0,150,214,0.3)] group"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0096D6]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#0096D6]/20 blur-3xl rounded-full -translate-y-16 translate-x-16 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform duration-700" />

              <div className="relative z-10">
                {/* Logo Section - Centered */}
                <div className="flex flex-col items-center mb-6">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5 }}
                    className="h-24 flex items-center justify-center"
                  >
                    <img src="/vendors/juniper.png" alt="Juniper" width={300} height={96} className="max-h-24 w-auto dark:invert drop-shadow-2xl" />
                  </motion.div>
                  <div className="mt-3 text-center">
                    <h3 className="text-2xl font-black text-black dark:text-white mb-1 tracking-tight">Juniper</h3>
                    <div className="h-1 w-12 mx-auto bg-gradient-to-r from-[#0096D6] to-[#00C8F0] rounded-full mb-2" />
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Routing & Switching</p>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-2.5">
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0096D6]/20 flex items-center justify-center group-hover/item:bg-[#0096D6]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#0096D6]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enterprise routing solutions</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0096D6]/20 flex items-center justify-center group-hover/item:bg-[#0096D6]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#0096D6]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">AI-driven operations</span>
                  </motion.li>
                  <motion.li
                    className="flex items-center gap-3 group/item"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0096D6]/20 flex items-center justify-center group-hover/item:bg-[#0096D6]/30 transition-colors">
                      <CheckCircle className="w-4 h-4 text-[#0096D6]" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Secure WAN connectivity</span>
                  </motion.li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Partnership Value */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-morphism-dark rounded-xl p-8 border border-purple-200/50 dark:border-purple-400/30 bg-purple-500/5 text-center"
          >
            <h3 className="text-2xl font-bold mb-4 text-black dark:text-white">
              Complete Technology Ecosystem
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6 max-w-3xl mx-auto">
              Experience how these industry leaders integrate to create comprehensive networking solutions. 
              Our demo lab showcases real-world scenarios with authentic vendor configurations.
            </p>
            <div className="flex justify-center items-center gap-8">
              <div className="text-center">
                <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <div className="text-sm font-medium text-black dark:text-white">Performance</div>
              </div>
              <div className="text-center">
                <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <div className="text-sm font-medium text-black dark:text-white">Security</div>
              </div>
              <div className="text-center">
                <Globe className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <div className="text-sm font-medium text-black dark:text-white">Scalability</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Enhanced CTA Section */}
        <div className="relative overflow-hidden">
          {/* Multi-layer gradient background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <div className="absolute top-0 left-0 w-full h-full opacity-30">
              <div className="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-float" />
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
            </div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 py-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Ready to Experience the Lab?
              </h2>
              <p className="text-xl mb-12 opacity-90 text-white max-w-3xl mx-auto">
                Access production-grade demonstrations and collaborate with our presales specialists.
                Schedule your session and discover how we support technology decisions across the ecosystem.
              </p>

              {/* CTA Options */}
              <div className="grid md:grid-cols-3 gap-8 mb-12 max-w-4xl mx-auto">
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="glass-morphism-dark rounded-xl p-6 border border-white/20 bg-white/10 text-center group cursor-pointer"
                  onClick={() => navigate('/booking')}
                >
                  <Sparkles className="w-12 h-12 text-yellow-400 mx-auto mb-4 group-hover:animate-pulse" />
                  <h3 className="text-xl font-bold text-white mb-2">Guided Session</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Expert-led demonstrations tailored to your requirements
                  </p>
                  <div className="text-green-400 font-bold">Schedule Now</div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="glass-morphism-dark rounded-xl p-6 border border-white/20 bg-white/10 text-center group cursor-pointer"
                  onClick={() => navigate('/demos')}
                >
                  <Globe className="w-12 h-12 text-blue-400 mx-auto mb-4 group-hover:animate-pulse" />
                  <h3 className="text-xl font-bold text-white mb-2">Platform Exploration</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Browse demos, resources, and technical documentation
                  </p>
                  <div className="text-green-400 font-bold">Browse Catalog</div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="glass-morphism-dark rounded-xl p-6 border border-white/20 bg-white/10 text-center group cursor-pointer"
                  onClick={() => navigate('/booking')}
                >
                  <Users className="w-12 h-12 text-purple-400 mx-auto mb-4 group-hover:animate-pulse" />
                  <h3 className="text-xl font-bold text-white mb-2">Group Workshop</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Multi-stakeholder sessions with collaborative exploration
                  </p>
                  <div className="text-green-400 font-bold">Contact Team</div>
                </motion.div>
              </div>

              {/* Primary CTA */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  onClick={() => navigate('/booking')}
                  className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-4 px-12 rounded-xl shadow-2xl text-lg group"
                >
                  <Sparkles className="mr-3 h-6 w-6 text-yellow-500" />
                  Schedule Your Lab Session
                  <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="mt-12 flex flex-wrap justify-center items-center gap-8 text-white/70"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm">Production Infrastructure</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm">Certified Specialists</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm">Ecosystem Support</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
